import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  Container, 
  Flex, 
  Card, 
  Text, 
  Button, 
  Heading, 
  Table, 
  Badge, 
  AlertDialog,
  Dialog,
  TextField,
  Checkbox,
  IconButton,
  Box,
  ScrollArea
} from '@radix-ui/themes';
import { 
  PlusIcon, 
  EyeOpenIcon, 
  EyeClosedIcon, 
  TrashIcon, 
  Pencil1Icon, 
  CheckIcon, 
  Cross1Icon,
  PersonIcon,
  QuestionMarkCircledIcon
} from '@radix-ui/react-icons';
import { getApiUrl, API_ENDPOINTS } from '../../../config/api.config';
import { toggleEnvironment } from '../../../utils/apiClient';
import { getAuthHeaders } from '../../../utils/api';
import refreshToken from '../../../utils/apiUtils';

const ClassDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [classData, setClassData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [availableStudents, setAvailableStudents] = useState([]);
  const [showAddStudentsModal, setShowAddStudentsModal] = useState(false);
  const [selectedStudents, setSelectedStudents] = useState([]);
  const [questions, setQuestions] = useState([]);
  const [showQuestions, setShowQuestions] = useState(true);
  const [showStudents, setShowStudents] = useState(true);
  const [isEditingName, setIsEditingName] = useState(false);
  const [editedName, setEditedName] = useState('');
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);

  // Get user data from localStorage
  const userData = localStorage.getItem('user');
  const user = userData ? JSON.parse(userData) : null;
  const schoolName = user?.school;

  useEffect(() => {
    // Check if we have a valid ID
    if (!id || id === 'undefined') {
      setError('Invalid class ID. Please go back and try again.');
      setLoading(false);
      return;
    }
    
    console.log(`Loading class with ID: ${id}`);
    fetchClassDetails();
    fetchClassQuestions();
  }, [id]);

  const fetchClassQuestions = async () => {
    try {
      const response = await axios.get(
        `/api/v1/stem@home/question/questions/class/${id}`,
        { 
          baseURL: toggleEnvironment(),
          headers: getAuthHeaders() 
        }
      );
      console.log("Question data response:", response.data);
      if (response.data.status === 'success') {
        setQuestions(response.data.data || []);
      }
    } catch (error) {
      console.error("Error fetching class questions:", error);
    }
  };

  const fetchClassDetails = async () => {
    try {
      console.log("Fetching class details with API_ENDPOINTS.CLASS.DETAILS");
      const response = await axios.get(
        getApiUrl(API_ENDPOINTS.CLASS.DETAILS(id)),
        { headers: getAuthHeaders() }
      );
      console.log("Class details response:", response.data);
      setClassData(response.data.class);
      setLoading(false);
    } catch (error) {
      console.error("Error fetching class details:", error);
      setError("Failed to load class details");
      setLoading(false);
    }
  };

  const fetchAvailableStudents = async () => {
    try {
      const response = await axios.get(
        getApiUrl(API_ENDPOINTS.TEACHER.SCHOOL_STUDENTS),
        { headers: getAuthHeaders() }
      );
      
      if (response.data.status === 'success') {
        // Filter out students who are already in the class
        const currentStudentIds = classData?.students.map(student => student._id) || [];
        const availableStudents = response.data.data.filter(student => 
          !currentStudentIds.includes(student._id)
        );
        setAvailableStudents(availableStudents);
      }
    } catch (error) {
      console.error("Error fetching available students:", error);
    }
  };

  const handleAddStudents = async () => {
    try {
      await axios.post(
        getApiUrl(API_ENDPOINTS.STUDENT.ADD_TO_CLASS(id)),
        {
          studentIds: selectedStudents
        },
        { headers: getAuthHeaders() }
      );

      // Reset state and refresh class details
      setSelectedStudents([]);
      setShowAddStudentsModal(false);
      fetchClassDetails();
    } catch (error) {
      console.error("Error adding students:", error);
      setError("Failed to add students to class");
    }
  };

  const handleStudentSelection = (studentId) => {
    setSelectedStudents(prev => {
      if (prev.includes(studentId)) {
        return prev.filter(id => id !== studentId);
      } else {
        return [...prev, studentId];
      }
    });
  };

  const handleRemoveStudent = async (studentId) => {
    try {
      await axios.post(
        getApiUrl(API_ENDPOINTS.STUDENT.REMOVE_FROM_CLASS),
        {
          classId: id,
          studentId: studentId
        },
        { headers: getAuthHeaders() }
      );
      fetchClassDetails();
    } catch (error) {
      console.error("Error removing student:", error);
      setError("Failed to remove student from class");
    }
  };

  const handleRemoveQuestion = async (questionId) => {
    try {
      const response = await axios.post(
        '/api/v1/stem@home/question/questions/remove-from-class',
        {
          questionId,
          classId: id
        },
        { 
          baseURL: toggleEnvironment(),
          headers: getAuthHeaders() 
        }
      );
      
      if (response.data.status === 'success') {
        // Update questions list
        setQuestions(questions.filter(q => q._id !== questionId));
      }
    } catch (error) {
      console.error("Error removing question from class:", error);
      setError("Failed to remove question from class");
    }
  };

  const handleDeleteClass = async () => {
    try {
      setDeleting(true);
      console.log('Deleting class:', id);
      
      const token = localStorage.getItem('token');
      const response = await axios.delete(
        `/api/v1/stem@home/class/${id}`,
        {
          baseURL: toggleEnvironment(),
          headers: {
            ...getAuthHeaders(),
            'Authorization': `Bearer ${token}`
          }
        }
      );

      if (response.data.status === 'success') {
        console.log('Class deleted successfully');
        navigate('/classes');
      } else {
        throw new Error(response.data.message || 'Failed to delete class');
      }
    } catch (err) {
      console.error("Error deleting class:", err);
      alert(err.response?.data?.message || 'Failed to delete class');
    } finally {
      setDeleting(false);
    }
  };

  const handleUpdateClassName = async () => {
    if (!editedName.trim()) {
      alert('Class name cannot be empty');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const response = await axios.put(
        `/api/v1/stem@home/class/${id}`,
        { name: editedName.trim() },
        {
          baseURL: toggleEnvironment(),
          headers: {
            ...getAuthHeaders(),
            'Authorization': `Bearer ${token}`
          }
        }
      );

      if (response.data.status === 'success') {
        setClassData(prev => ({ ...prev, name: editedName.trim() }));
        setIsEditingName(false);
        console.log('Class name updated successfully');
      } else {
        throw new Error(response.data.message || 'Failed to update class name');
      }
    } catch (err) {
      console.error("Error updating class name:", err);
      alert(err.response?.data?.message || 'Failed to update class name');
    }
  };

  const startEditingName = () => {
    setEditedName(classData.name);
    setIsEditingName(true);
  };

  const cancelEditingName = () => {
    setIsEditingName(false);
    setEditedName('');
  };

  const openAddStudentsModal = () => {
    fetchAvailableStudents();
    setShowAddStudentsModal(true);
  };

  if (loading) {
    return (
      <Container style={{ maxWidth: '100%', width: '100%' }} p="4">
        <Card>
          <Flex align="center" justify="center" p="8">
            <Text color="gray">Loading class details...</Text>
          </Flex>
        </Card>
      </Container>
    );
  }

  if (error) {
    return (
      <Container style={{ maxWidth: '100%', width: '100%' }} p="4">
        <Card>
          <Flex direction="column" align="center" gap="4" p="8">
            <Heading size="6" color="red">Error</Heading>
            <Text>{error}</Text>
            <Flex gap="3">
              <Button 
                onClick={() => navigate('/classes')}
                style={{ backgroundColor: 'var(--yellow-9)', color: 'var(--gray-12)' }}
              >
                Back to Classes
              </Button>
              <Button
                variant="soft"
                onClick={() => {
                  setError(null);
                  setLoading(true);
                  fetchClassDetails();
                }}
              >
                Retry
              </Button>
            </Flex>
          </Flex>
        </Card>
      </Container>
    );
  }

  if (!classData) {
    return (
      <Container style={{ maxWidth: '100%', width: '100%' }} p="4">
        <Card>
          <Flex direction="column" align="center" gap="4" p="8">
            <Heading size="6">Class not found</Heading>
            <Text>The requested class could not be found. It may have been deleted or you might not have permission to view it.</Text>
            <Button 
              onClick={() => navigate('/classes')}
              style={{ backgroundColor: 'var(--yellow-9)', color: 'var(--gray-12)' }}
            >
              Back to Classes
            </Button>
          </Flex>
        </Card>
      </Container>
    );
  }

  return (
    <Container style={{ maxWidth: '100%', width: '100%' }} p="4">
      <Flex direction="column" gap="4">
        {/* Header Section */}
        <Card>
          <Flex direction="column" gap="4" p="4">
            <Flex justify="between" align="center">
              {isEditingName ? (
                <Flex align="center" gap="2" style={{ flex: 1 }}>
                  <TextField.Root
                    style={{ flex: 1 }}
                    value={editedName}
                    onChange={(e) => setEditedName(e.target.value)}
                    placeholder="Class name"
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        handleUpdateClassName();
                      } else if (e.key === 'Escape') {
                        cancelEditingName();
                      }
                    }}
                  />
                  <IconButton
                    variant="soft"
                    color="green"
                    onClick={handleUpdateClassName}
                  >
                    <CheckIcon />
                  </IconButton>
                  <IconButton
                    variant="soft"
                    color="gray"
                    onClick={cancelEditingName}
                  >
                    <Cross1Icon />
                  </IconButton>
                </Flex>
              ) : (
                <Flex align="center" gap="2" style={{ flex: 1, alignItems: 'baseline' }}>
                  <Heading size="8">{classData.name}</Heading>
                  <IconButton
                    variant="ghost"
                    onClick={startEditingName}
                    size="1"
                    style={{ alignSelf: 'baseline' }}
                  >
                    <Pencil1Icon width="16" height="16" />
                  </IconButton>
                </Flex>
              )}
              
              <Flex align="center" gap="2">
                <Button
                  variant="solid"
                  onClick={() => setShowQuestions(!showQuestions)}
                  style={{ backgroundColor: 'black', color: 'white' }}
                >
                  {showQuestions ? <EyeClosedIcon /> : <EyeOpenIcon />}
                  {showQuestions ? 'Hide Questions' : 'Show Questions'}
                </Button>
                <IconButton
                  variant="soft"
                  color="red"
                  onClick={() => setDeleteDialogOpen(true)}
                >
                  <TrashIcon />
                </IconButton>
              </Flex>
            </Flex>
          </Flex>
        </Card>

        {/* Questions Section */}
        {showQuestions && (
          <Card>
            <Flex direction="column" gap="4" p="4">
              <Flex justify="between" align="center">
                <Flex align="center" gap="2" style={{ alignItems: 'baseline' }}>
                  <QuestionMarkCircledIcon width="16" height="16" />
                  <Heading size="6">Questions ({questions.length})</Heading>
                </Flex>
                <Button 
                  onClick={() => navigate('/view-questions')}
                  style={{ backgroundColor: 'var(--yellow-9)', color: 'var(--gray-12)' }}
                >
                  <PlusIcon />
                  Add Question
                </Button>
              </Flex>
              
              {questions.length === 0 ? (
                <Flex align="center" justify="center" p="8">
                  <Text color="gray">No questions assigned to this class yet.</Text>
                </Flex>
              ) : (
                <Table.Root>
                  <Table.Header>
                    <Table.Row>
                      <Table.ColumnHeaderCell>#</Table.ColumnHeaderCell>
                      <Table.ColumnHeaderCell>Question Text</Table.ColumnHeaderCell>
                      <Table.ColumnHeaderCell>Total Marks</Table.ColumnHeaderCell>
                      <Table.ColumnHeaderCell>Mark Range</Table.ColumnHeaderCell>
                      <Table.ColumnHeaderCell>Actions</Table.ColumnHeaderCell>
                    </Table.Row>
                  </Table.Header>
                  <Table.Body>
                    {questions.map((question, index) => (
                      <Table.Row key={question._id}>
                        <Table.Cell>
                          <Text weight="medium">{index + 1}</Text>
                        </Table.Cell>
                        <Table.Cell>{question.question}</Table.Cell>
                        <Table.Cell>{question.marks}</Table.Cell>
                        <Table.Cell>
                          <Flex align="center" gap="1" wrap="wrap">
                            <Badge 
                              variant="soft" 
                              color="red"
                              style={{ fontSize: '12px' }}
                            >
                              {question.lowMarkRange}
                            </Badge>
                            {question.mediumMarkRange && (
                              <>
                                <Text size="1" color="gray">-</Text>
                                <Badge 
                                  variant="soft" 
                                  color="yellow"
                                  style={{ fontSize: '12px' }}
                                >
                                  {question.mediumMarkRange}
                                </Badge>
                              </>
                            )}
                            <Text size="1" color="gray">-</Text>
                            <Badge 
                              variant="soft" 
                              color="green"
                              style={{ fontSize: '12px' }}
                            >
                              {question.highMarkRange}
                            </Badge>
                          </Flex>
                        </Table.Cell>
                        <Table.Cell>
                          <IconButton
                            variant="ghost"
                            color="red"
                            onClick={() => handleRemoveQuestion(question._id)}
                          >
                            <TrashIcon />
                          </IconButton>
                        </Table.Cell>
                      </Table.Row>
                    ))}
                  </Table.Body>
                </Table.Root>
              )}
            </Flex>
          </Card>
        )}

        {/* Students Section */}
        <Card>
          <Flex direction="column" gap="4" p="4">
            <Flex justify="between" align="center">
              <Flex align="center" gap="2" style={{ alignItems: 'baseline' }}>
                <PersonIcon width="16" height="16" />
                <Heading size="6">Students ({classData.students.length})</Heading>
              </Flex>
              <Button 
                onClick={openAddStudentsModal}
                style={{ backgroundColor: 'var(--yellow-9)', color: 'var(--gray-12)' }}
              >
                <PlusIcon />
                Add Student
              </Button>
            </Flex>
            
            {classData.students.length === 0 ? (
              <Flex align="center" justify="center" p="8">
                <Text color="gray">No students in this class yet.</Text>
              </Flex>
            ) : (
              <Table.Root>
                <Table.Header>
                  <Table.Row>
                    <Table.ColumnHeaderCell>Student Name</Table.ColumnHeaderCell>
                    <Table.ColumnHeaderCell>Student Email</Table.ColumnHeaderCell>
                    <Table.ColumnHeaderCell>Actions</Table.ColumnHeaderCell>
                  </Table.Row>
                </Table.Header>
                <Table.Body>
                  {classData.students.map((student) => (
                    <Table.Row key={student._id || student.id}>
                      <Table.Cell>{student.name}</Table.Cell>
                      <Table.Cell>{student.email}</Table.Cell>
                      <Table.Cell>
                        <IconButton
                          variant="ghost"
                          color="red"
                          onClick={() => handleRemoveStudent(student._id || student.id)}
                        >
                          <TrashIcon />
                        </IconButton>
                      </Table.Cell>
                    </Table.Row>
                  ))}
                </Table.Body>
              </Table.Root>
            )}
          </Flex>
        </Card>
      </Flex>

      {/* Add Students Modal */}
      <Dialog.Root open={showAddStudentsModal} onOpenChange={setShowAddStudentsModal}>
        <Dialog.Content style={{ maxWidth: 600 }}>
          <Dialog.Title>Add Students to Class</Dialog.Title>
          <Dialog.Description size="2">
            Select students to add to "{classData.name}". You can select multiple students.
          </Dialog.Description>

          <Flex direction="column" gap="4" mt="4">
            {availableStudents.length === 0 ? (
              <Text color="gray">No available students to add.</Text>
            ) : (
              <ScrollArea style={{ maxHeight: 300 }}>
                <Flex direction="column" gap="2">
                  {availableStudents.map((student) => (
                    <Flex key={student._id || student.id} align="center" gap="2" p="2">
                      <Checkbox
                        checked={selectedStudents.includes(student._id || student.id)}
                        onCheckedChange={() => handleStudentSelection(student._id || student.id)}
                      />
                      <Flex direction="column" gap="1">
                        <Text weight="medium">{student.name}</Text>
                        <Text size="2" color="gray">{student.email}</Text>
                      </Flex>
                    </Flex>
                  ))}
                </Flex>
              </ScrollArea>
            )}
          </Flex>

          <Flex gap="3" mt="4" justify="end">
            <Dialog.Close>
              <Button variant="soft" color="gray">
                Cancel
              </Button>
            </Dialog.Close>
            <Button
              onClick={handleAddStudents}
              disabled={selectedStudents.length === 0}
              style={{ backgroundColor: 'var(--yellow-9)', color: 'var(--gray-12)' }}
            >
              Add {selectedStudents.length > 0 ? `${selectedStudents.length} ` : ''}{selectedStudents.length === 1 ? 'Student' : 'Students'}
            </Button>
          </Flex>
        </Dialog.Content>
      </Dialog.Root>

      {/* Delete Confirmation Dialog */}
      <AlertDialog.Root open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialog.Content style={{ maxWidth: 450 }}>
          <AlertDialog.Title>Delete Class</AlertDialog.Title>
          <AlertDialog.Description size="2">
            Are you sure you want to delete the class "{classData.name}"?
            This action cannot be undone and will remove all associated questions and submissions.
          </AlertDialog.Description>

          <Flex gap="3" mt="4" justify="end">
            <AlertDialog.Cancel>
              <Button variant="soft" color="gray">
                Cancel
              </Button>
            </AlertDialog.Cancel>
            <AlertDialog.Action>
              <Button 
                variant="solid" 
                color="red"
                onClick={handleDeleteClass}
                disabled={deleting}
              >
                {deleting ? 'Deleting...' : 'Delete'}
              </Button>
            </AlertDialog.Action>
          </Flex>
        </AlertDialog.Content>
      </AlertDialog.Root>
    </Container>
  );
};

export default ClassDetails; 